import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BusinessIcon from '@mui/icons-material/Business'
import DomainIcon from '@mui/icons-material/Domain'
import ErrorIcon from '@mui/icons-material/Error'
import GavelIcon from '@mui/icons-material/Gavel'
import BadgeIcon from '@mui/icons-material/Badge'
import {
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { ReactElement } from 'react'
import { ArtisanWithEfacture } from './EinvoicingArtisansCtrl'

interface Props {
  artisan: ArtisanWithEfacture
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: ReactElement
  label: string
  value?: string | null
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {icon}

      <div style={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>

        <Typography variant="body1" fontWeight={500}>
          {value || 'Non renseigné'}
        </Typography>
      </div>
    </Stack>
  )
}

export default function IsFacturXReadyContent({
  artisan,
}: Props): ReactElement {
  const checks = [
    {
      label: 'Raison sociale',
      ok: !!(artisan.legalName || artisan.companyName),
    },
    {
      label: 'SIREN',
      ok: !!artisan.SIREN,
    },
    {
      label: 'SIRET',
      ok: !!artisan.SIRET,
    },
    {
      label: 'RCS',
      ok: !!artisan.rcs,
    },
  ]

  const completed = checks.filter((c) => c.ok).length
  const total = checks.length
  const progress = (completed / total) * 100
  const ready = completed === total

  return (
    <Card
      elevation={4}
      sx={{
        width: '100%',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={4}>
          {/* En-tête */}
          <div>
            <Typography variant="h5" fontWeight={700}>
              Préparation à la facturation électronique
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Vérification des informations de l'entreprise avant l'activation.
            </Typography>
          </div>

          <Divider />

          {/* Société */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BusinessIcon color="primary" />

              <Typography variant="h6">
                {artisan.legalName || artisan.companyName}
              </Typography>
            </Stack>

            <Chip
              color={ready ? 'success' : 'warning'}
              label={ready ? 'Prête' : 'À compléter'}
            />
          </Stack>

          {/* Progression */}
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">
                Niveau de préparation
              </Typography>

              <Typography
                variant="subtitle2"
                fontWeight={600}
              >
                {completed}/{total}
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progress}
              color={ready ? 'success' : 'warning'}
              sx={{
                height: 10,
                borderRadius: 5,
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {Math.round(progress)} % des informations requises sont
              renseignées.
            </Typography>
          </Stack>

          {/* Informations */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<BadgeIcon color="action" />}
                label="SIREN"
                value={artisan.SIREN}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<DomainIcon color="action" />}
                label="SIRET"
                value={artisan.SIRET}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<GavelIcon color="action" />}
                label="RCS"
                value={artisan.rcs}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Résumé */}
          <Alert severity={ready ? 'success' : 'warning'}>
            <AlertTitle>
              {ready
                ? 'Entreprise prête'
                : 'Des informations sont manquantes'}
            </AlertTitle>

            {ready
              ? "Toutes les informations essentielles sont présentes. L'entreprise peut être configurée pour la facturation électronique."
              : "Complétez les éléments signalés ci-dessous avant de poursuivre."}
          </Alert>

          {/* Checklist */}
          <List dense disablePadding>
            {checks.map((check) => (
              <ListItem key={check.label}>
                <ListItemIcon>
                  {check.ok ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={check.label}
                  secondary={
                    check.ok
                      ? 'Information renseignée'
                      : 'Information manquante'
                  }
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </CardContent>
    </Card>
  )
}